import { defineComponent as _defineComponent } from 'vue'
import { vModelText as _vModelText, normalizeClass as _normalizeClass, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, resolveComponent as _resolveComponent, withCtx as _withCtx, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = ["readonly", "disabled", "placeholder"]
const _hoisted_2 = ["popovertarget", "disabled"]
const _hoisted_3 = ["id"]
const _hoisted_4 = /*#__PURE__*/_createElementVNode("hr", { class: "divider" }, null, -1)
const _hoisted_5 = { class: "current-month-year" }
const _hoisted_6 = { class: "month" }
const _hoisted_7 = { class: "year" }
const _hoisted_8 = { class: "calendar" }
const _hoisted_9 = { class: "weekdays" }
const _hoisted_10 = { class: "weekday-text" }
const _hoisted_11 = { class: "weekday-text" }
const _hoisted_12 = { class: "weekday-text" }
const _hoisted_13 = { class: "weekday-text" }
const _hoisted_14 = { class: "weekday-text" }
const _hoisted_15 = { class: "weekday-text" }
const _hoisted_16 = { class: "weekday-text" }
const _hoisted_17 = { class: "calendar-grid" }
const _hoisted_18 = ["data-date", "aria-selected", "onClick"]
const _hoisted_19 = /*#__PURE__*/_createElementVNode("hr", { class: "divider" }, null, -1)
const _hoisted_20 = { class: "calendar-navigation" }

import Icon from "@common/components/Icon.vue";
import { useContentLanguages } from "@common/composable/useMetaDataStore";
import { useUserSettings } from "@common/composable/useRootStore";
import { Guid } from "@util/guid";
import _ from "lodash";
import moment, { Moment, unitOfTime } from "moment";
import { computed, nextTick, reactive, ref, watch } from "vue";

//#region Store

const DEFAULT_DATE_V_MODEL_FORMAT = "YYYY-MM-DD";

//#endregion

//#region General
type CalendarGridModel = { day: number; isInCurrentMonth: boolean; dateString: string };


export default /*#__PURE__*/_defineComponent({
  __name: 'MDatePicker',
  props: {
    value: {},
    buttonOnly: { type: Boolean },
    readonly: { type: Boolean },
    disabled: { type: Boolean },
    placeholder: {},
    isInvalid: { type: Boolean }
  },
  emits: ["input", "change"],
  setup(__props: any, { expose: __expose, emit: emits }) {

const props = __props;

const { userSettings } = useUserSettings();
const { contentLanguages } = useContentLanguages();

//#endregion

//#region Constants
const id = Guid.new();
const datePickerButton = ref<HTMLButtonElement | null>(null);
const datePickerMenu = ref<HTMLDivElement | null>(null);
const datePickerInput = ref<HTMLInputElement | null>(null);




const localeDateFormat = computed(() =>
  contentLanguages.value.available.find((x) => x.languageCode == userSettings.value.CurrentLocale)?.shortDateFormat.toUpperCase()
);

//#endregion

//#region Viewmodel
const localValue = ref<Moment | null>(null);
const lastValidValue = ref<Moment | null>(null);

const menuPosition = reactive({ x: 0, y: 0 });
const isOpen = ref(false);

const navigationDate = ref(moment());
const today = moment();
//#endregion

//#region LocalValue

const inputValue = computed({
  get: () => {
    return localValue.value?.format(localeDateFormat.value);
  },
  set: (nv) => {
    if (nv === "" || !nv) {
      localValue.value = null;
    } else {
      const date = moment(nv, localeDateFormat.value);

      if (date.isValid()) {
        navigationDate.value = date;
        localValue.value = date;
        lastValidValue.value = date;
      } else {
        localValue.value = _.cloneDeep(lastValidValue.value);
        if (datePickerInput.value && !lastValidValue.value) datePickerInput.value.value = "";
      }
    }
  }
});

watch(
  () => props.value,
  (nv) => {
    if (nv === "" || !nv) {
      localValue.value = null;
    } else {
      const date = moment(nv);

      if (date.isValid()) {
        navigationDate.value = date;
        localValue.value = date;
        lastValidValue.value = date;
      } else {
        localValue.value = _.cloneDeep(lastValidValue.value);
        if (datePickerInput.value && !lastValidValue.value) datePickerInput.value.value = "";
      }
    }
  },
  { immediate: true }
);
watch(
  localValue,
  (nv) => {
    emits("input", nv?.format(DEFAULT_DATE_V_MODEL_FORMAT));
  },
  { deep: true }
);
//#endregion

//#region VisualContruction

const toggleHandler = (event: ToggleEvent) => {
  isOpen.value = event.newState == "open";
  calculatePositionOfMenu();
};

const calculatePositionOfMenu = () => {
  menuPosition.x = 0;
  menuPosition.y = 0;
  nextTick(() => {
    if (datePickerMenu.value && datePickerButton.value) {
      const toggleRect = datePickerButton.value.getBoundingClientRect();
      const menuRect = datePickerMenu.value.getBoundingClientRect();
      const x = toggleRect.x - menuRect.right + toggleRect.width;
      let y = toggleRect.y - menuRect.y + toggleRect.height;
      if (menuRect.bottom + y > window.innerHeight) {
        y = toggleRect.y - menuRect.bottom;
      }
      menuPosition.x = x;
      menuPosition.y = y;
    }
  });
};

const calculatedGridArray = computed(() => {
  const result: Array<CalendarGridModel[]> = [];
  const manipulationDate = moment(navigationDate.value);
  manipulationDate.locale("de");

  const endOfMonth = moment(manipulationDate.endOf("month"));
  const startOfMonth = moment(manipulationDate.startOf("month"));

  for (let i = 0; i <= 5; i++) {
    const week: CalendarGridModel[] = [];
    for (let i = 0; i <= 6; i++) {
      const entryDate = manipulationDate.weekday(i);
      entryDate.add(1, "second");
      const entry: CalendarGridModel = {
        day: parseInt(entryDate.format("DD")),
        isInCurrentMonth: entryDate.isBetween(startOfMonth, endOfMonth),
        dateString: entryDate.format(DEFAULT_DATE_V_MODEL_FORMAT)
      };
      week.push(entry);
    }
    result.push(week);
    manipulationDate.add(1, "week");
  }
  return result;
});
//#endregion

//#region Interaction

const toggle = () => {
  datePickerMenu.value?.togglePopover(!isOpen.value);
};
const show = () => {
  datePickerMenu.value?.showPopover();
};
const hide = () => {
  datePickerMenu.value?.hidePopover();
};

const selectEntry = (entry: CalendarGridModel) => {
  if (entry.isInCurrentMonth) {
    localValue.value = moment(entry.dateString);
    emits("change", localValue.value?.format(DEFAULT_DATE_V_MODEL_FORMAT));
    hide();
  }
};

const switchDate = (amount: number, unitOfTime: unitOfTime.Base) => {
  navigationDate.value = moment(navigationDate.value.add(amount, unitOfTime));
};

const jumptoToday = () => {
  navigationDate.value = moment();
};

//#endregion

//#region Expose
__expose({
  toggle,
  hide,
  show
});
//#endregion

return (_ctx: any,_cache: any) => {
  const _component_b_btn = _resolveComponent("b-btn")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["date-picker", { 'read-only': _ctx.readonly, disabled: _ctx.disabled, danger: _ctx.isInvalid, 'is-invalid': _ctx.isInvalid }])
  }, [
    (!_ctx.buttonOnly)
      ? _withDirectives((_openBlock(), _createElementBlock("input", {
          key: 0,
          class: _normalizeClass(["date-picker-input", { 'read-only': _ctx.readonly, disabled: _ctx.disabled, 'border-danger': _ctx.isInvalid }]),
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((inputValue).value = $event)),
          ref_key: "datePickerInput",
          ref: datePickerInput,
          readonly: _ctx.readonly,
          disabled: _ctx.disabled,
          placeholder: _ctx.placeholder
        }, null, 10, _hoisted_1)), [
          [
            _vModelText,
            inputValue.value,
            void 0,
            { lazy: true }
          ]
        ])
      : _createCommentVNode("", true),
    _createElementVNode("button", {
      class: _normalizeClass(["date-picker-button", { disabled: _ctx.disabled }]),
      popovertarget: `date-picker-${_unref(id)}`,
      ref_key: "datePickerButton",
      ref: datePickerButton,
      disabled: _ctx.disabled
    }, [
      _createVNode(Icon, { icon: "Calendar" })
    ], 10, _hoisted_2),
    _createElementVNode("div", {
      id: `date-picker-${_unref(id)}`,
      class: _normalizeClass(["date-picker-menu", { 'is-open': isOpen.value }]),
      popover: "",
      onToggle: toggleHandler,
      ref_key: "datePickerMenu",
      ref: datePickerMenu,
      style: _normalizeStyle({
        transform: `translate(${menuPosition.x}px, ${menuPosition.y}px)`
      })
    }, [
      _createElementVNode("div", {
        class: _normalizeClass(["current-selection", { 'has-value': localValue.value }])
      }, _toDisplayString(localValue.value ? _ctx.$d(localValue.value.toDate(), "longDateWithWeekDay") : _ctx.$t("noDateSelected")), 3),
      _hoisted_4,
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("div", _hoisted_6, _toDisplayString(_ctx.$t(`${navigationDate.value.format("MMMM").toLowerCase()}`)), 1),
        _createElementVNode("div", _hoisted_7, _toDisplayString(navigationDate.value.format("YYYY")), 1)
      ]),
      _createElementVNode("div", _hoisted_8, [
        _createElementVNode("div", _hoisted_9, [
          _createElementVNode("div", _hoisted_10, _toDisplayString(_ctx.$t("weekday.mon")), 1),
          _createElementVNode("div", _hoisted_11, _toDisplayString(_ctx.$t("weekday.tue")), 1),
          _createElementVNode("div", _hoisted_12, _toDisplayString(_ctx.$t("weekday.wed")), 1),
          _createElementVNode("div", _hoisted_13, _toDisplayString(_ctx.$t("weekday.thu")), 1),
          _createElementVNode("div", _hoisted_14, _toDisplayString(_ctx.$t("weekday.fri")), 1),
          _createElementVNode("div", _hoisted_15, _toDisplayString(_ctx.$t("weekday.sat")), 1),
          _createElementVNode("div", _hoisted_16, _toDisplayString(_ctx.$t("weekday.sun")), 1)
        ]),
        _createElementVNode("div", _hoisted_17, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(calculatedGridArray.value, (row, rowIndex) => {
            return (_openBlock(), _createElementBlock("div", {
              class: "calendar-grid-row",
              key: `row-${rowIndex}`
            }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(row, (entry, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  "data-date": entry.dateString,
                  "aria-selected": entry.dateString == localValue.value?.format(DEFAULT_DATE_V_MODEL_FORMAT),
                  class: _normalizeClass(["calendar-grid-entry", {
                'current-month': entry.isInCurrentMonth,
                today: _unref(today).format(DEFAULT_DATE_V_MODEL_FORMAT) == entry.dateString,
                selected: entry.dateString == localValue.value?.format(DEFAULT_DATE_V_MODEL_FORMAT)
              }]),
                  key: `entry-${rowIndex}-${index}`,
                  onClick: ($event: any) => (selectEntry(entry))
                }, _toDisplayString(entry.day), 11, _hoisted_18))
              }), 128))
            ]))
          }), 128))
        ])
      ]),
      _hoisted_19,
      _createElementVNode("div", _hoisted_20, [
        _createVNode(_component_b_btn, {
          class: "btn-icon mono pre-year",
          onClick: _cache[1] || (_cache[1] = ($event: any) => (switchDate(-1, 'year'))),
          title: _ctx.$t('datepicker.lastYear')
        }, {
          default: _withCtx(() => [
            _createVNode(Icon, { icon: "ChevronDoubleLeft" })
          ]),
          _: 1
        }, 8, ["title"]),
        _createVNode(_component_b_btn, {
          class: "btn-icon mono pre-month",
          onClick: _cache[2] || (_cache[2] = ($event: any) => (switchDate(-1, 'month'))),
          title: _ctx.$t('datepicker.lastMonth')
        }, {
          default: _withCtx(() => [
            _createVNode(Icon, { icon: "ChevronLeft" })
          ]),
          _: 1
        }, 8, ["title"]),
        _createVNode(_component_b_btn, {
          class: "btn-icon mono to-today",
          onClick: jumptoToday,
          title: _ctx.$t('datepicker.currentMonth')
        }, {
          default: _withCtx(() => [
            _createVNode(Icon, { icon: "CurrentMonthSheet" })
          ]),
          _: 1
        }, 8, ["title"]),
        _createVNode(_component_b_btn, {
          class: "btn-icon mono post-year",
          onClick: _cache[3] || (_cache[3] = ($event: any) => (switchDate(1, 'month'))),
          title: _ctx.$t('datepicker.nextMonth')
        }, {
          default: _withCtx(() => [
            _createVNode(Icon, { icon: "ChevronRight" })
          ]),
          _: 1
        }, 8, ["title"]),
        _createVNode(_component_b_btn, {
          class: "btn-icon mono post-month",
          onClick: _cache[4] || (_cache[4] = ($event: any) => (switchDate(1, 'year'))),
          title: _ctx.$t('datepicker.nextYear')
        }, {
          default: _withCtx(() => [
            _createVNode(Icon, { icon: "ChevronDoubleRight" })
          ]),
          _: 1
        }, 8, ["title"])
      ])
    ], 46, _hoisted_3)
  ], 2))
}
}

})